import { Reveal } from "./Reveal";
import { Star } from "./Logo";
import { Gauge, Compass, MessageCircleHeart, Crown, Sunrise, Heart, Share2, BadgeCheck, Timer, Swords, Mic, PersonStanding, History, Gift, UserRound, Store } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const ICONS = [Gauge, Compass, MessageCircleHeart, Crown, Sunrise, Heart, Share2, BadgeCheck];
const MORE_ICONS = [Timer, Swords, Mic, PersonStanding, History, Gift, UserRound, Store];
const ACCENTS = ["var(--rose-soft)", "var(--surface-secondary)", "var(--surface-secondary)", "var(--rose-soft)", "var(--surface-secondary)", "var(--rose-soft)", "var(--surface-secondary)", "var(--surface-secondary)"];

export default function Features() {
  const { t } = useLang();
  return (
    <section id="features" className="py-24 sm:py-36" style={{ background: "var(--surface-secondary)" }} data-testid="features-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
              <Star size={11} /> {t.features.kicker}
            </p>
            <h2 className="mt-5 max-w-xl font-serif-display text-4xl sm:text-5xl font-semibold leading-tight" data-testid="features-title">
              {t.features.title}
            </h2>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {t.features.cards.map((card, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal
                key={i}
                delay={(i % 4) * 0.08}
                className="group rounded-3xl border p-7 transition-shadow duration-500 hover:shadow-[0_24px_48px_-24px_rgba(198,156,109,0.4)]"
                style={{ background: "var(--surface)", borderColor: "var(--line)" }}
                data-testid={`feature-card-${i + 1}`}
              >
                <span
                  className="inline-flex h-12 w-12 items-center justify-center rounded-2xl transition-transform duration-500 group-hover:-translate-y-1 group-hover:rotate-3"
                  style={{ background: ACCENTS[i] }}
                >
                  <Icon size={22} strokeWidth={1.6} style={{ color: "var(--camel)" }} />
                </span>
                <h3 className="mt-6 font-serif-display text-xl font-semibold leading-snug">{card.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>
                  {card.text}
                </p>
              </Reveal>
            );
          })}
        </div>

        {t.features.more && (
          <div className="mt-16" data-testid="features-more">
            <Reveal>
              <h3 className="font-serif-display text-2xl sm:text-3xl font-semibold">{t.features.more.title}</h3>
            </Reveal>
            <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {t.features.more.items.map((item, i) => {
                const Icon = MORE_ICONS[i];
                return (
                  <Reveal
                    key={i}
                    delay={(i % 4) * 0.06}
                    className="flex items-start gap-3.5 rounded-2xl border px-5 py-4 transition-shadow duration-500 hover:shadow-[0_18px_36px_-20px_rgba(198,156,109,0.4)]"
                    style={{ background: "var(--surface)", borderColor: "var(--line)" }}
                    data-testid={`feature-more-${i + 1}`}
                  >
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: "var(--rose-soft)" }}>
                      <Icon size={17} strokeWidth={1.7} style={{ color: "var(--camel)" }} />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold">{item.t}</span>
                      <span className="mt-1 block text-[13px] leading-relaxed" style={{ color: "var(--muted-warm)" }}>{item.d}</span>
                    </span>
                  </Reveal>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
